import React, { useEffect, useMemo, useState } from 'react';
import { Activity } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import adminOpsService from '../../services/adminOpsService';
import { copyTextToClipboard } from '../../utils/fundingClipboardUtils';
import { buildAuthorizedQaProxyRunCommand } from '../../services/funding/fundingAuthorizedQaProxyService';
import { TERMINAL_TARGET_UNIX } from '../../services/funding/fundingBrowserProfilesService';

const GEO_DISCLAIMER = 'Approximate IP geolocation. It does not identify a physical residential street address.';
const POLL_MS = 2000;

export function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

export function displayResultLabel(item) {
  if (item?.displayResult) return item.displayResult;
  const status = item?.status || '';
  if (status === 'complete' || status === 'completed') {
    if (item?.grade === 'pass') return 'Complete — Pass';
    if (item?.grade === 'warning') return 'Complete — Warning';
    if (item?.grade === 'fail') return 'Complete — Fail';
    return 'Complete';
  }
  if (status === 'failed' || status === 'error') return 'Failed';
  if (status === 'expired') return 'Expired';
  if (status === 'cancelled') return 'Cancelled';
  if (status === 'collecting_signals') return 'Collecting signals';
  return 'Waiting for browser';
}

function targetingSummary(item) {
  const target = item?.targeting || item?.selectedTarget || {};
  const parts = [
    target.countryLabel || target.country,
    target.state,
    target.city,
    target.zip,
    target.asn && target.asn !== '__all_asns__' ? `ASN ${target.asn}` : '',
  ].filter((part) => part && part !== '__all_states__' && part !== '__all_cities__');
  return parts.join(' · ') || item?.targetLocation || '—';
}

function assignmentOptionLabel(item) {
  return [
    item.clientLabel || 'Client',
    item.clientId,
    item.profileId,
    item.targetLocation,
  ].filter(Boolean).join(' | ');
}

function remainingLabel(expiresAt) {
  if (!expiresAt) return '';
  const expires = new Date(expiresAt).getTime();
  if (Number.isNaN(expires)) return '';
  const remaining = Math.max(0, Math.floor((expires - Date.now()) / 1000));
  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function privacyLabel(value) {
  if (value === 'pass') return 'Pass';
  if (value === 'warning') return 'Warning';
  if (value === 'unavailable') return 'Unavailable';
  return value || 'Unavailable';
}

function Field({ label, value, testId }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">{label}</p>
      <p className="text-sm text-slate-200 break-words" data-testid={testId}>{value || 'Unavailable'}</p>
    </div>
  );
}

export default function QaDiagnosticsPanel({
  auth,
  busy,
  setBusy,
  runs,
  assignments,
  onRefresh,
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [emptyOpen, setEmptyOpen] = useState(false);
  const [assignmentId, setAssignmentId] = useState('');
  const [assignmentQuery, setAssignmentQuery] = useState('');
  const [waiting, setWaiting] = useState(null);
  const [report, setReport] = useState(null);
  const [filters, setFilters] = useState({
    client: '',
    result: '',
    country: '',
    state: '',
    city: '',
    platformAdmin: '',
    dateFrom: '',
    dateTo: '',
  });
  const [now, setNow] = useState(Date.now());

  const [liveAssignments, setLiveAssignments] = useState(null);
  const assignmentList = liveAssignments || assignments;
  const selectedAssignment = useMemo(
    () => assignmentList.find((item) => item.id === assignmentId) || null,
    [assignmentList, assignmentId],
  );
  const filteredAssignments = useMemo(() => {
    const query = assignmentQuery.trim().toLowerCase();
    if (!query) return assignmentList;
    return assignmentList.filter((item) => assignmentOptionLabel(item).toLowerCase().includes(query));
  }, [assignmentList, assignmentQuery]);

  const visibleRuns = useMemo(() => {
    return (runs || []).filter((item) => {
      if (filters.client) {
        const haystack = `${item.clientLabel || ''} ${item.clientId || ''}`.toLowerCase();
        if (!haystack.includes(filters.client.toLowerCase())) return false;
      }
      if (filters.result && !displayResultLabel(item).toLowerCase().includes(filters.result.toLowerCase()) && item.status !== filters.result) {
        return false;
      }
      if (filters.country && !`${item.targetCountry || ''} ${item.targetLocation || ''}`.toLowerCase().includes(filters.country.toLowerCase())) {
        return false;
      }
      if (filters.state && !`${item.targetState || ''} ${item.targetLocation || ''}`.toLowerCase().includes(filters.state.toLowerCase())) {
        return false;
      }
      if (filters.city && !`${item.targetCity || ''} ${item.targetLocation || ''}`.toLowerCase().includes(filters.city.toLowerCase())) {
        return false;
      }
      if (filters.platformAdmin) {
        const haystack = `${item.platformAdminDisplayName || item.actorDisplayName || ''} ${item.platformAdminUserId || item.actorUserId || ''}`.toLowerCase();
        if (!haystack.includes(filters.platformAdmin.toLowerCase())) return false;
      }
      if (filters.dateFrom && item.createdAt && new Date(item.createdAt) < new Date(filters.dateFrom)) return false;
      if (filters.dateTo && item.createdAt && new Date(item.createdAt) > new Date(`${filters.dateTo}T23:59:59`)) return false;
      return true;
    });
  }, [runs, filters]);

  useEffect(() => {
    if (!waiting) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [waiting]);

  useEffect(() => {
    if (!waiting?.run?.id) return undefined;
    const inFlight = ['waiting_for_browser', 'collecting_signals', 'pending'].includes(waiting.run.status);
    if (!inFlight) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        auth();
        const payload = await adminOpsService.getQaDiagnosticsRunStatus(waiting.run.id);
        if (cancelled) return;
        setWaiting((current) => (current ? { ...current, run: payload } : current));
        if (payload.status === 'complete' && payload.reportReady) {
          const reportPayload = await adminOpsService.getQaDiagnosticsReport(waiting.run.id);
          if (!cancelled) setReport(reportPayload);
        }
        if (['complete', 'failed', 'expired', 'cancelled'].includes(payload.status)) {
          onRefresh?.();
        }
      } catch {
        /* keep polling until cancel/unmount */
      }
    };
    poll();
    const timer = window.setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [waiting?.run?.id, waiting?.run?.status]);

  const startRun = async (id) => {
    setBusy(true);
    try {
      auth();
      const payload = await adminOpsService.startQaDiagnosticsRun(id);
      const runCommand = buildAuthorizedQaProxyRunCommand({
        token: payload.launchToken,
        scriptFilename: payload.scriptFilename,
        terminal: TERMINAL_TARGET_UNIX,
      });
      setWaiting({
        ...payload,
        runCommand,
        run: payload.run,
      });
      setPickerOpen(false);
      setEmptyOpen(false);
      toast.success('Waiting for the local diagnostic browser.');
      await onRefresh?.();
    } catch (err) {
      toast.error(String(err?.message || err || 'Unable to start QA diagnostics.'));
    } finally {
      setBusy(false);
    }
  };

  const handleRunDiagnostics = async () => {
    auth();
    let items = assignments;
    try {
      const payload = await adminOpsService.getQaDiagnosticsAssignments();
      items = payload?.items || [];
    } catch (err) {
      toast.error(String(err?.message || err || 'Unable to load assignments.'));
      return;
    }
    setLiveAssignments(items);
    if (!items.length) {
      setEmptyOpen(true);
      setPickerOpen(false);
      return;
    }
    setEmptyOpen(false);
    setAssignmentQuery('');
    if (items.length === 1) {
      setAssignmentId(items[0].id);
    } else {
      setAssignmentId('');
    }
    setPickerOpen(true);
  };

  const copyLaunchCommand = async () => {
    if (!waiting?.runCommand) return;
    try {
      await copyTextToClipboard(waiting.runCommand);
      toast.success('Run command copied');
    } catch {
      toast.error('Unable to copy the run command');
    }
  };

  const cancelRun = async () => {
    if (!waiting?.run?.id) {
      setWaiting(null);
      return;
    }
    setBusy(true);
    try {
      auth();
      const payload = await adminOpsService.cancelQaDiagnosticsRun(waiting.run.id);
      setWaiting((current) => (current ? { ...current, run: payload } : current));
      await onRefresh?.();
    } catch (err) {
      toast.error(String(err?.message || err || 'Unable to cancel diagnostics.'));
    } finally {
      setBusy(false);
    }
  };

  const retryFrom = async (runId) => {
    setBusy(true);
    try {
      auth();
      const payload = await adminOpsService.retryQaDiagnosticsRun(runId);
      const runCommand = buildAuthorizedQaProxyRunCommand({
        token: payload.launchToken,
        scriptFilename: payload.scriptFilename,
        terminal: TERMINAL_TARGET_UNIX,
      });
      setReport(null);
      setWaiting({
        ...payload,
        runCommand,
        run: payload.run,
      });
      toast.success('New diagnostic run created.');
      await onRefresh?.();
    } catch (err) {
      toast.error(String(err?.message || err || 'Unable to retry diagnostics.'));
    } finally {
      setBusy(false);
    }
  };

  const openReport = async (runId) => {
    setBusy(true);
    try {
      auth();
      const payload = await adminOpsService.getQaDiagnosticsReport(runId);
      if (!payload.reportReady && ['waiting_for_browser', 'collecting_signals', 'pending'].includes(payload.status)) {
        setWaiting((current) => ({
          ...(current || {}),
          run: payload,
          runCommand: current?.run?.id === runId ? current.runCommand : current?.runCommand,
        }));
        return;
      }
      setReport(payload);
    } catch (err) {
      toast.error(String(err?.message || err || 'Unable to open diagnostics report.'));
    } finally {
      setBusy(false);
    }
  };

  const waitingStatus = waiting?.run?.status || 'waiting_for_browser';
  const showRetry = ['failed', 'expired'].includes(waitingStatus);
  const countdown = remainingLabel(waiting?.launchExpiresAt || waiting?.run?.launchExpiresAt);
  void now;

  return (
    <>
      <div className="bg-stone-900 border border-stone-800 rounded-xl overflow-x-auto" data-testid="qa-diagnostics-history">
        <div className="p-5 border-b border-stone-800 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-white">QA Diagnostics history</h2>
            <p className="text-sm text-slate-500 mt-1">
              Platform-admin reports from the FundElite diagnostics page. {GEO_DISCLAIMER} Exit IP and full fingerprint stay in the report, not this table.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleRunDiagnostics}
            disabled={busy}
            data-testid="qa-diagnostics-run"
          >
            <Activity size={16} className="mr-2" />
            Run QA Diagnostics
          </Button>
        </div>
        <form className="p-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="qa-diagnostics-filters">
          <input className="bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white" placeholder="Client" value={filters.client} onChange={(event) => setFilters((current) => ({ ...current, client: event.target.value }))} data-testid="qa-diagnostics-filter-client" />
          <select className="bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white" value={filters.result} onChange={(event) => setFilters((current) => ({ ...current, result: event.target.value }))} data-testid="qa-diagnostics-filter-result">
            <option value="">All results</option>
            <option value="complete">Complete</option>
            <option value="failed">Failed</option>
            <option value="expired">Expired</option>
            <option value="cancelled">Cancelled</option>
            <option value="waiting_for_browser">Waiting for browser</option>
          </select>
          <input className="bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white" placeholder="Target country" value={filters.country} onChange={(event) => setFilters((current) => ({ ...current, country: event.target.value }))} data-testid="qa-diagnostics-filter-country" />
          <input className="bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white" placeholder="Target state" value={filters.state} onChange={(event) => setFilters((current) => ({ ...current, state: event.target.value }))} data-testid="qa-diagnostics-filter-state" />
          <input className="bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white" placeholder="Target city" value={filters.city} onChange={(event) => setFilters((current) => ({ ...current, city: event.target.value }))} data-testid="qa-diagnostics-filter-city" />
          <input className="bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white" placeholder="Platform admin" value={filters.platformAdmin} onChange={(event) => setFilters((current) => ({ ...current, platformAdmin: event.target.value }))} data-testid="qa-diagnostics-filter-admin" />
          <input type="date" className="bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white" value={filters.dateFrom} onChange={(event) => setFilters((current) => ({ ...current, dateFrom: event.target.value }))} data-testid="qa-diagnostics-filter-from" />
          <input type="date" className="bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white" value={filters.dateTo} onChange={(event) => setFilters((current) => ({ ...current, dateTo: event.target.value }))} data-testid="qa-diagnostics-filter-to" />
        </form>
        <table className="w-full text-sm">
          <thead className="text-left text-slate-500 border-b border-stone-800">
            <tr>
              <th className="p-3">Run time</th>
              <th className="p-3">Client label</th>
              <th className="p-3">Client ID</th>
              <th className="p-3">QA assignment ID</th>
              <th className="p-3">Browser profile ID</th>
              <th className="p-3">Target location</th>
              <th className="p-3">Observed country / region</th>
              <th className="p-3">ASN</th>
              <th className="p-3">Result</th>
              <th className="p-3">Report</th>
            </tr>
          </thead>
          <tbody>
            {visibleRuns.map((item) => (
              <tr key={item.id} className="border-b border-stone-800/70 text-slate-200">
                <td className="p-3">{formatDate(item.createdAt)}</td>
                <td className="p-3">{item.clientLabel || '—'}</td>
                <td className="p-3">{item.clientId || '—'}</td>
                <td className="p-3">{item.assignmentId || '—'}</td>
                <td className="p-3">{item.profileId || '—'}</td>
                <td className="p-3">{item.targetLocation || '—'}</td>
                <td className="p-3">{[item.observedCountry, item.observedRegion].filter(Boolean).join(' / ') || '—'}</td>
                <td className="p-3">{item.observedAsn || '—'}</td>
                <td className="p-3">{displayResultLabel(item)}</td>
                <td className="p-3">
                  <button
                    type="button"
                    className="text-sky-400"
                    onClick={() => openReport(item.id)}
                    data-testid={`qa-diagnostics-report-${item.id}`}
                  >
                    Open report
                  </button>
                </td>
              </tr>
            ))}
            {!visibleRuns.length ? (
              <tr>
                <td className="p-6 text-slate-500" colSpan={10}>No QA diagnostics runs yet.</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {emptyOpen ? (
        <div className="bg-stone-900 border border-stone-800 rounded-xl p-5 space-y-3" data-testid="qa-diagnostics-empty">
          <h2 className="text-lg font-semibold text-white">Run QA Diagnostics</h2>
          <p className="text-sm text-slate-300">No active Authorized QA Proxy assignment is available for diagnostics.</p>
          <p className="text-sm text-slate-400">
            Configure an Authorized QA Proxy assignment in Funding HQ first, then return here to run diagnostics.
          </p>
          <Button variant="outline" onClick={() => setEmptyOpen(false)}>Close</Button>
        </div>
      ) : null}

      {pickerOpen ? (
        <div className="bg-stone-900 border border-stone-800 rounded-xl p-5 space-y-3" data-testid="qa-diagnostics-assignment-picker">
          <h2 className="text-lg font-semibold text-white">Select Authorized QA Proxy assignment</h2>
          <p className="text-sm text-slate-400">
            Orlando or any other city is only the selected DataImpulse target. Each report stays attached to one assignment, client, profile, and run.
          </p>
          {assignmentList.length > 1 ? (
            <>
              <input
                className="bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white w-full"
                placeholder="Search client, ID, profile, or target"
                value={assignmentQuery}
                onChange={(event) => setAssignmentQuery(event.target.value)}
                data-testid="qa-diagnostics-assignment-search"
              />
              <select
                className="bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white w-full"
                value={assignmentId}
                onChange={(event) => setAssignmentId(event.target.value)}
                data-testid="qa-diagnostics-assignment-select"
              >
                <option value="">Select an assignment</option>
                {filteredAssignments.map((item) => (
                  <option key={item.id} value={item.id}>{assignmentOptionLabel(item)}</option>
                ))}
              </select>
            </>
          ) : null}
          {selectedAssignment ? (
            <div className="bg-stone-950 border border-stone-800 rounded-lg p-4 space-y-1 text-sm text-slate-300" data-testid="qa-diagnostics-assignment-preview">
              <p>Client label: {selectedAssignment.clientLabel || '—'}</p>
              <p>Client ID: {selectedAssignment.clientId || '—'}</p>
              <p>QA assignment ID: {selectedAssignment.id}</p>
              <p>Browser profile ID: {selectedAssignment.profileId || '—'}</p>
              <p>Selected country/state/city/ZIP/ASN: {targetingSummary(selectedAssignment)}</p>
            </div>
          ) : null}
          <div className="flex gap-2">
            <Button
              onClick={() => startRun(assignmentId)}
              disabled={busy || !assignmentId}
              data-testid="qa-diagnostics-assignment-confirm"
            >
              Start diagnostic run
            </Button>
            <Button variant="outline" onClick={() => setPickerOpen(false)}>Cancel</Button>
          </div>
        </div>
      ) : null}

      {waiting ? (
        <div className="bg-stone-900 border border-amber-500/30 rounded-xl p-5 space-y-3" data-testid="qa-diagnostics-waiting-modal">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Waiting for diagnostic browser</h2>
            <Button variant="outline" onClick={() => setWaiting(null)}>Close</Button>
          </div>
          <ol className="text-sm text-slate-300 list-decimal pl-5 space-y-1">
            <li>Copy and run the command in macOS/Linux Terminal.</li>
            <li>A fresh Authorized QA Proxy browser opens FundElite Diagnostics automatically.</li>
            <li>This page updates when diagnostics finish.</li>
          </ol>
          <p className="text-sm text-slate-300">Selected client: {waiting.clientLabel || waiting.run?.clientLabel || '—'}</p>
          <p className="text-sm text-slate-300">Selected target location: {waiting.targetLocation || waiting.run?.targetLocation || '—'}</p>
          <p className="text-sm text-amber-100" data-testid="qa-diagnostics-expiry">Token expiry: {countdown || '—'}</p>
          <p className="text-sm text-slate-200" data-testid="qa-diagnostics-waiting-status">
            Current state: {waiting.run?.displayStatus || displayResultLabel(waiting.run)}
          </p>
          <pre className="bg-stone-950 border border-stone-800 rounded-lg p-3 text-xs text-slate-200 overflow-x-auto" data-testid="qa-diagnostics-run-command">
            <code>{waiting.runCommand}</code>
          </pre>
          <div className="flex flex-wrap gap-2">
            <Button onClick={copyLaunchCommand} data-testid="qa-diagnostics-copy-command">Copy run command</Button>
            {['waiting_for_browser', 'collecting_signals'].includes(waitingStatus) ? (
              <Button variant="outline" onClick={cancelRun} disabled={busy} data-testid="qa-diagnostics-cancel">Cancel run</Button>
            ) : null}
            {showRetry ? (
              <Button onClick={() => retryFrom(waiting.run.id)} disabled={busy} data-testid="qa-diagnostics-retry">
                {waitingStatus === 'expired' ? 'Create new diagnostic run' : 'Retry diagnostics'}
              </Button>
            ) : null}
          </div>
          {waitingStatus === 'expired' ? (
            <p className="text-sm text-amber-100">{waiting.run?.failureReason || 'The diagnostic browser was not started before the single-use launch token expired.'}</p>
          ) : null}
          {waitingStatus === 'failed' ? (
            <p className="text-sm text-red-200">{waiting.run?.failureReason || 'Diagnostics failed.'}</p>
          ) : null}
          {waitingStatus === 'cancelled' ? (
            <p className="text-sm text-slate-300">Result: Cancelled</p>
          ) : null}
        </div>
      ) : null}

      {report?.reportReady ? (
        <QaDiagnosticsReport report={report} onClose={() => setReport(null)} onRetry={() => retryFrom(report.id || report.runId)} />
      ) : null}
    </>
  );
}

export function QaDiagnosticsReport({ report, onClose, onRetry }) {
  const status = report.status;
  const geo = report.approximateGeo || {};
  const classification = report.proxyClassification || {};
  const env = report.browserEnvironment || report.browserSignals || {};
  const leaks = report.leakResults || {};
  const target = report.selectedTarget || {};

  return (
    <div className="bg-stone-900 border border-stone-800 rounded-xl p-5 space-y-4" data-testid="qa-diagnostics-admin-report">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">Diagnostics report</h2>
        <Button variant="outline" onClick={onClose}>Close</Button>
      </div>
      <p className="text-xs text-amber-100" data-testid="qa-diagnostics-admin-disclaimer">{report.geoDisclaimer || GEO_DISCLAIMER}</p>
      <p className="text-sm text-slate-200" data-testid="qa-diagnostics-admin-result">Result: {displayResultLabel(report)}</p>

      {status === 'failed' ? (
        <div className="space-y-2">
          <p className="text-sm text-red-200">{report.failureReason || 'Diagnostics failed.'}</p>
          <Button onClick={onRetry} data-testid="qa-diagnostics-report-retry">Retry diagnostics</Button>
        </div>
      ) : null}
      {status === 'expired' ? (
        <div className="space-y-2">
          <p className="text-sm text-amber-100">The diagnostic browser was not started before the single-use launch token expired.</p>
          <Button onClick={onRetry} data-testid="qa-diagnostics-report-retry">Create new diagnostic run</Button>
        </div>
      ) : null}
      {status === 'cancelled' ? (
        <p className="text-sm text-slate-300">This run was cancelled and is not a pending report.</p>
      ) : null}

      {status === 'complete' ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Client label" value={report.clientLabel} />
            <Field label="Client ID" value={report.clientId} />
            <Field label="QA assignment ID" value={report.assignmentId} />
            <Field label="Browser profile ID" value={report.profileId} />
            <Field label="Diagnostic run ID" value={report.runId || report.id} />
            <Field label="Platform admin" value={report.platformAdminDisplayName} />
            <Field label="Run started" value={formatDate(report.createdAt)} />
            <Field label="Run completed" value={formatDate(report.completedAt)} />
            <Field label="Selected country" value={target.countryLabel || target.country} />
            <Field label="Selected state" value={target.state} />
            <Field label="Selected city" value={target.city} />
            <Field label="Selected ZIP" value={target.zip} />
            <Field label="Selected ASN" value={target.asn} />
            <Field label="Targeting mode" value={target.mode} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white mb-2">Observed network result</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Public exit IP" value={report.exitIp} testId="qa-diagnostics-admin-exit-ip" />
              <Field label="Observed country" value={geo.country} />
              <Field label="Observed region/state" value={geo.region} />
              <Field label="Observed city" value={geo.city} />
              <Field label="Observed postal code" value={geo.postal} />
              <Field label="ASN" value={geo.asn} />
              <Field label="Network owner / ISP" value={geo.org} />
              <Field label="Network type" value={classification.networkType} />
              <Field label="Proxy / privacy classification" value={classification.proxyClassification || classification.label} />
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white mb-2">Browser environment</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Browser OS" value={env.os} />
              <Field label="Browser family/version" value={[env.browserFamily, env.browserVersion].filter(Boolean).join(' ')} />
              <Field label="Timezone" value={env.timezone} />
              <Field label="Locale and languages" value={[env.locale, ...(env.languages || [])].filter(Boolean).join(', ')} />
              <Field label="Screen" value={env.screenWidth ? `${env.screenWidth}×${env.screenHeight}` : (report.browserSignals?.screen ? `${report.browserSignals.screen.width}×${report.browserSignals.screen.height}` : '')} />
              <Field label="Outer window" value={env.outerWindowWidth ? `${env.outerWindowWidth}×${env.outerWindowHeight}` : ''} />
              <Field label="Inner viewport" value={env.innerViewportWidth ? `${env.innerViewportWidth}×${env.innerViewportHeight}` : (report.browserSignals?.viewport ? `${report.browserSignals.viewport.width}×${report.browserSignals.viewport.height}` : '')} />
              <Field label="Device pixel ratio" value={env.devicePixelRatio != null ? String(env.devicePixelRatio) : ''} />
              <Field label="Color depth" value={env.colorDepth != null ? String(env.colorDepth) : ''} />
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white mb-2">Privacy and route checks</h3>
            <p className="text-sm text-slate-300">WebRTC: {privacyLabel(leaks.webrtc)}{leaks.webrtcReason ? ` — ${leaks.webrtcReason}` : ''}</p>
            <p className="text-sm text-slate-300">DNS: {privacyLabel(leaks.dns)}{leaks.dnsReason ? ` — ${leaks.dnsReason}` : ''}</p>
            <p className="text-sm text-slate-300">IPv6: {privacyLabel(leaks.ipv6)}{leaks.ipv6Reason ? ` — ${leaks.ipv6Reason}` : ''}</p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white mb-2">Target consistency</h3>
            <p className="text-sm text-slate-200">{(report.consistency?.status || report.grade || '').toString()}</p>
            <ul className="space-y-1 text-sm text-slate-400">
              {(report.consistency?.explanations || report.findings || []).map((item) => (
                <li key={typeof item === 'string' ? item : `${item.code}-${item.message}`}>{typeof item === 'string' ? item : item.message}</li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
}
